import { useEffect, useState, useCallback } from "react";
import { Trash2 } from "lucide-react";
import { api, MXN } from "@/lib/api";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUSES = ["Pendiente", "Confirmada", "Finalizada", "Cancelada"];
const STATUS_STYLE = {
  Pendiente: "bg-status-pending/15 text-status-pending border-status-pending/30",
  Confirmada: "bg-status-confirmed/15 text-status-confirmed border-status-confirmed/30",
  Finalizada: "bg-status-finished/15 text-status-finished border-status-finished/30",
  Cancelada: "bg-status-cancelled/15 text-status-cancelled border-status-cancelled/30",
};

export default function Agenda() {
  const [appts, setAppts] = useState([]);
  const [filterStatus, setFilterStatus] = useState("Todas");
  const [filterDate, setFilterDate] = useState("");

  const load = useCallback(() => {
    const params = {};
    if (filterStatus !== "Todas") params.status = filterStatus;
    if (filterDate) params.date = filterDate;
    api.get("/admin/appointments", { params }).then((r) => setAppts(r.data)).catch(() => {});
  }, [filterStatus, filterDate]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/appointments/${id}`, { status });
      toast.success("Estado actualizado");
      load();
    } catch {
      toast.error("No se pudo actualizar");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/appointments/${id}`);
      toast.success("Cita eliminada");
      load();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  return (
    <div data-testid="admin-agenda" className="space-y-6">
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="font-serif-display text-3xl">Agenda</h1>
          <p className="text-sm text-muted-foreground">{appts.length} citas</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="date"
            data-testid="agenda-filter-date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            className="border border-border bg-card px-3 py-2 text-sm"
          />
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="agenda-filter-status" className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Todas">Todas</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          {filterDate && (
            <button onClick={() => setFilterDate("")} className="text-sm text-muted-foreground hover:text-primary">
              Limpiar
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto border border-border">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Fecha / Hora</th>
              <th className="px-4 py-3">Cliente</th>
              <th className="px-4 py-3">Servicio</th>
              <th className="px-4 py-3">Especialista</th>
              <th className="px-4 py-3">Total</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {appts.map((a) => (
              <tr key={a.id} data-testid={`appt-row-${a.id}`} className="border-b border-border last:border-0">
                <td className="px-4 py-3 whitespace-nowrap">
                  <div>{a.date}</div>
                  <div className="text-xs text-muted-foreground">{a.time}</div>
                </td>
                <td className="px-4 py-3">
                  <div>{a.client_name}</div>
                  <div className="text-xs text-muted-foreground">{a.client_phone}</div>
                </td>
                <td className="px-4 py-3">{a.service_name}</td>
                <td className="px-4 py-3">{a.stylist_name}</td>
                <td className="px-4 py-3 text-primary">{MXN(a.price)}</td>
                <td className="px-4 py-3">
                  <Select value={a.status} onValueChange={(v) => updateStatus(a.id, v)}>
                    <SelectTrigger
                      data-testid={`appt-status-${a.id}`}
                      className={`h-8 w-32 border text-xs ${STATUS_STYLE[a.status]}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3">
                  <button
                    data-testid={`appt-delete-${a.id}`}
                    onClick={() => remove(a.id)}
                    className="text-muted-foreground transition-colors hover:text-destructive"
                  >
                    <Trash2 size={16} strokeWidth={1.5} />
                  </button>
                </td>
              </tr>
            ))}
            {appts.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">No hay citas.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
